import React, { useState, useEffect } from 'react';
import { Box, Typography, CardContent, Stack, Button, Alert, Tabs, Tab } from '@mui/material';
import {
  ArrowBack as BackIcon
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useGetRefundQuery } from '../../../services/RefundService';
import { useAppDispatch, useAppSelector } from '../../../app/store';
import { resetRefundAction as resetRefund, forceResetRefundAction as forceResetRefund, updateRefundAction as updateRefund, restoreFromBackupAction as restoreFromBackup, clearBackupAction as clearBackup } from '../../../services/RefundService/slice';

import { PatientInfoView } from '../components';
import { RefundDetailsSection, CheckInformationSection, NotesSection, DocumentsSection, ApprovalsSection } from '../components/sections';
import RefundEditHeader from '../components/RefundEditHeader';
import PageCard from '../../common/components/styled-ui-elements/PageCard';
import { PageLoading as Loading } from '../../common/components';

// Constants
const PAGE_STYLES = {
  container: {
    minHeight: '100vh',
    py: 1.5,
    px: 2,
  },
  header: {
    backgroundColor: '#ffffff',
    borderBottom: '2px solid #3b82f6',
    p: 1.5
  },
  saveButton: {
    backgroundColor: '#162346',
    '&:hover': {
      backgroundColor: '#1a2a52',
    },
    fontWeight: 600
  }
} as const;

// Tab Panel Interface and Component
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const CustomTabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`refund-tabpanel-${index}`}
    aria-labelledby={`refund-tab-${index}`}
  >
    {value === index && (
      <Box sx={{ pt: 3, pb: 1.5, px: 1 }}>
        {children}
      </Box>
    )}
  </div>
);

// Types
interface RefundEditParams {
  company: string | null;
  center: string | null;
  id: string | null;
}

// Custom hooks
const useRefundEditParams = (): RefundEditParams => {
  const [searchParams] = useSearchParams();
  return {
    company: searchParams.get('company'),
    center: searchParams.get('center'),
    id: searchParams.get('id')
  };
};

const useRefundEditLogic = (params: RefundEditParams) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { currentRefund, isEdited } = useAppSelector(state => state.refund);
  
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);

  const { company, center, id } = params;
  const hasRequiredParams = Boolean(company && center && id);

  // Tab change handler
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // API queries and mutations
  const { 
    data: refundData, 
    isLoading: isLoadingRefund, 
    error: refundError 
  } = useGetRefundQuery(
    { company: company!, center: center!, id: id! },
    { skip: !hasRequiredParams }
  );

  const handleBack = () => navigate(-1); // Go back to previous page

  return {
    currentRefund,
    isEdited,
    error,
    setError,
    isLoadingRefund,
    refundError,
    refundData,
    hasRequiredParams,
    tabValue,
    handleTabChange,
    handleBack
  };
};

// Error boundary components
const ErrorState: React.FC<{ message: string; onBack: () => void }> = ({ message, onBack }) => (
  <Box sx={{ p: 3 }}>
    <Alert severity="error">{message}</Alert>
    <Button variant="contained" onClick={onBack} sx={{ mt: 2 }}>
      Back to Refunds
    </Button>
  </Box>
);

// Section components

// Main component
export const RefundEdit: React.FC = () => {
  const params = useRefundEditParams();
  const {
    currentRefund,
    isEdited,
    error,
    setError,
    isLoadingRefund,
    refundError,
    refundData,
    hasRequiredParams,
    tabValue,
    handleTabChange,
    handleBack
  } = useRefundEditLogic(params);

  // Early returns for error states
  if (!hasRequiredParams) {
    const { company, center, id } = params;
    return (
      <ErrorState
        message={`Missing required parameters. Expected: company, center, and id.
Current URL params: company=${company || 'missing'}, center=${center || 'missing'}, id=${id || 'missing'}`}
        onBack={handleBack}
      />
    );
  }

  if (isLoadingRefund || (!refundData && !currentRefund)) {
    return <Loading />;
  }

  if (refundError) {
    return (
      <ErrorState
        message="Failed to load refund request. Please try again."
        onBack={handleBack}
      />
    );
  }

  // Get the refund data from state or API
  const refund = currentRefund || refundData;

  // Main render
  return (
    <Box sx={PAGE_STYLES.container}>
      <Box display="flex" justifyContent="center">
        <PageCard>
          <RefundEditHeader
            id={params.id!}
            onBack={handleBack}
            onError={setError}
          />
          
          <CardContent sx={{ p: 3, backgroundColor: '#ffffff' }}>
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Stack spacing={3}>
              <PatientInfoView />
              <RefundDetailsSection />
              
              {/* Tab Strip Section */}
              <Box>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <Tabs 
                    value={tabValue} 
                    onChange={handleTabChange} 
                    aria-label="refund tabs"
                    sx={{
                      minHeight: '40px',
                      '& .MuiTab-root': {
                        minHeight: '40px',
                        padding: '6px 16px',
                        fontSize: '0.875rem'
                      }
                    }}
                  >
                    <Tab label="Check Info" />
                    <Tab label="Notes" />
                    <Tab label="Documents" />
                    <Tab label="Approvals" />
                  </Tabs>
                </Box>
                
                <CustomTabPanel value={tabValue} index={0}>
                  <CheckInformationSection />
                </CustomTabPanel>
                
                <CustomTabPanel value={tabValue} index={1}>
                  <NotesSection />
                </CustomTabPanel>
                
                <CustomTabPanel value={tabValue} index={2}>
                  {(currentRefund || refundData) && (
                    <DocumentsSection refund={(currentRefund || refundData)!} />
                  )}
                </CustomTabPanel>
                
                <CustomTabPanel value={tabValue} index={3}>
                  <ApprovalsSection />
                </CustomTabPanel>
              </Box>
            </Stack>
          </CardContent>
        </PageCard>
      </Box>
    </Box>
  );
};
